import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { CheckCircle2, MailCheck, MailWarning, XCircle } from "lucide-react";
import { toast } from "sonner";
import { authLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Legacy link-based email verification page.
 * New flows use OTP at signup / login. Token links from old emails still work.
 * Visits without a token redirect to /auth (OTP is handled there on login).
 */
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const EmailVerification = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const tokenFromLink = query.get("token") || "";
  const {
    user,
    verifyEmailAsync,
    isVerifyingEmail,
  } = useAuth();
  const [isAutoVerifying, setIsAutoVerifying] = useState(false);
  const [autoVerifyDone, setAutoVerifyDone] = useState(false);
  const [verifyResult, setVerifyResult] = useState<
    | { status: "success"; alreadyVerified: boolean }
    | { status: "error" }
    | null
  >(null);

  const { language } = useLanguage();
  const labels = useMemo(() => authLabels(language), [language]);

  // No token → OTP is done at login; send user to auth.
  useEffect(() => {
    if (!tokenFromLink) {
      navigate("/auth", { replace: true });
    }
  }, [tokenFromLink, navigate]);

  useEffect(() => {
    const autoVerify = async () => {
      if (!tokenFromLink || autoVerifyDone) return;
      setIsAutoVerifying(true);
      try {
        const result = await verifyEmailAsync(tokenFromLink);
        const alreadyVerified = Boolean(
          (result as { alreadyVerified?: boolean } | undefined)?.alreadyVerified
        );
        setVerifyResult({ status: "success", alreadyVerified });

        if (alreadyVerified) {
          toast.info(labels.emailVerification?.alreadyVerified);
        } else {
          toast.success(labels.success?.emailVerifiedShort);
        }
      } catch {
        setVerifyResult({ status: "error" });
        toast.error(labels.error?.emailVerificationFailed);
      } finally {
        setIsAutoVerifying(false);
        setAutoVerifyDone(true);
      }
    };
    void autoVerify();
  }, [tokenFromLink, autoVerifyDone, verifyEmailAsync, labels]);

  if (!tokenFromLink) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-6 space-y-4">
        <div className="flex items-center gap-2 text-xl font-semibold">
          {verifyResult?.status === "success" ? (
            <MailCheck className="h-6 w-6" />
          ) : verifyResult?.status === "error" ? (
            <XCircle className="h-6 w-6 text-destructive" />
          ) : (
            <MailWarning className="h-6 w-6" />
          )}
          <span>{labels.emailVerification?.title || "Verifiera din e-post"}</span>
        </div>

        {isAutoVerifying || isVerifyingEmail ? (
          <p className="text-sm text-muted-foreground">
            {labels.emailVerification?.verifying || "Verifierar..."}
          </p>
        ) : null}

        {verifyResult?.status === "success" ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              <p className="text-sm">
                {verifyResult.alreadyVerified
                  ? labels.emailVerification?.alreadyVerified
                  : labels.success?.emailVerifiedShort}
              </p>
            </div>
            <Button className="w-full" onClick={() => navigate("/auth", { replace: true })}>
              {labels.buttons?.login || "Logga in"}
            </Button>
          </div>
        ) : null}

        {verifyResult?.status === "error" ? (
          <div className="space-y-3">
            <p className="text-sm text-destructive">
              {labels.error?.emailVerificationFailed}
            </p>
            <Button className="w-full" variant="outline" onClick={() => navigate("/auth", { replace: true })}>
              {labels.buttons?.login || "Logga in"}
            </Button>
          </div>
        ) : null}

        {!verifyResult && !isAutoVerifying && user ? (
          <Button className="w-full" onClick={() => navigate("/dashboard", { replace: true })}>
            {labels.buttons?.goToDashboard || "Till dashboard"}
          </Button>
        ) : null}
      </Card>
    </div>
  );
};

export default EmailVerification;
